<template>
  <div>
    <div
      class="modal fade overflow-hidden"
      id="mapModal"
      tabindex="-1"
      aria-labelledby="mapModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-fullscreen">
        <div class="modal-content">
          <div class="modal-body position-relative p-0">
            <!-- Map container -->
            <div
              id="map"
              ref="mapContainer"
              style="width: 100%; height:100vh; position: relative;"
            >
              <!-- Live stats row + elevation profile with a playback-synced head dot (matches the
                   iOS/Android flyover). Replaces the old cut-off Distance/Elevation box. -->
              <FlyoverElevationProfile
                v-if="showLabels && flyoverCoords.length > 1"
                :coords="flyoverCoords"
                :breaks="flyoverBreaks"
                :progress="flyoverProgress"
                :distance-label="String(currentDistance)"
                :elevation-label="String(currentElevation)"
              />
            </div>
            <div v-if="mapInitialized" class="flyover-toolbar">
              <div class="flyover-primary-buttons">
                <button class="btn btn-secondary flyover-pill-button" @click="openMapsDialog">
                  Maps
                </button>

                <button
                  v-if="isRecordedTrail || hikeTrailerId"
                  class="btn btn-primary flyover-pill-button"
                  @click="openShareVideo"
                >
                  <span class="material-icons" style="font-size: 18px; vertical-align: middle; margin-right: 6px;">movie</span>
                  Share video
                </button>
              </div>

              <div class="speed-buttons">
                <button
                  v-for="speed in speedOptions"
                  :key="speed"
                  :class="['btn', 'flyover-pill-button', playbackSpeed === speed ? 'btn-primary' : 'btn-secondary']"
                  @click="setPlaybackSpeed(speed)"
                >
                  {{ speed }}x
                </button>
              </div>

              <div v-if="animationController" class="control-buttons">
                <button class="btn btn-secondary rounded-circle d-flex align-items-center control-button" @click="handleRewind">
                  <span class="material-icons">fast_rewind</span>
                </button>

                <button class="btn btn-primary rounded-circle play-button d-flex align-items-center control-button" @click="togglePlayback">
                  <span v-if="isPlaying" class="material-icons">pause</span>
                  <span v-else class="material-icons">play_arrow</span>
                </button>

                <button class="btn btn-secondary rounded-circle d-flex align-items-center control-button" @click="handleFastForward">
                  <span class="material-icons">fast_forward</span>
                </button>
              </div>
            </div>

            <button class="btn btn-secondary rounded-circle d-flex align-items-center control-button close-button" data-bs-dismiss="modal">
              <span class="material-icons">close</span>
            </button>
          </div>
        </div>
      </div>
      <LoadingDialog :show-loading-dialog="loading"/>
    </div>
    <Teleport to="body">
      <BaseMapStylesDialog
        dialog-id="flyover-map-styles-dialog"
        map-instance-name="flyover_map"
        :maps-only="true"
        :show3-d-toggle="false"
        :force-full-style-reload="true"
      />
    </Teleport>
  </div>
</template>

<script>
import mapboxgl from "mapbox-gl";
import $ from "jquery";
// Import the previewTrail helper (ensure the path is correct)
import { previewTrail } from "../../utils/helpers/TrailPreview";
import { buildFlyoverRoute } from "../../utils/helpers/flyoverRoute";

// GraphQL clients/queries
import graph_client from "../../utils/graphql/graph_client";
import { GetHike } from "../../utils/graphql/hikes-graph";
import { GetPtv2PlannedTrailLineString } from "../../utils/graphql/planned-trail-graph";
import { GetRecordedTrailLineStringById } from "../../utils/graphql/recorded-trail-graph";
import { MeasurementPrefUtility } from "../../utils/helpers/MeasurementPref";
import LoadingDialog from "../../utils/shared_components/LoadingDialog.vue";
import { Modal } from "bootstrap";
import { claimReviewPromptTrigger } from '../../utils/reviewPromptTrigger';
import BaseMapStylesDialog from "../base_map_dialog/BaseMapStylesDialog.vue";
import FlyoverElevationProfile from "./FlyoverElevationProfile.vue";
import { resolveFlyoverMapInit } from "./flyoverMapInit";
import { buildHikeShareConfig, hikeShareStats } from "../flyover_share/trailerShare";
import { HiikerMapStyle } from "../../utils/models/MapStyles";
import { OnBoardingGoal } from "../../utils/models/OnBoardingGoal";

// Analytics entry point for every share opened from this screen (see flyover_share/shareAnalytics.ts).
// All three of openShareVideo's paths — hike trailer, passed-in recording config, page dataset — are
// the same entry point as far as "where did this share start" goes.
const SHARE_SOURCE = "trail_3d_preview";

export default {
  name: "FlyoverPreview",
  components: {LoadingDialog, BaseMapStylesDialog, FlyoverElevationProfile},
  props: {
    title: { type: String, default: "3D Flyover" }
  },
  data() {
    return {
      loading: false,
      mapInitialized: false,
      // True once the fullscreen modal has finished opening (its container is at final size). Map
      // creation waits for this so Mapbox measures the real size instead of its 400x300 default.
      modalShown: false,
      defaultCenter: [-74.5, 40],
      featureCollection: null,
      segments: [], // Array of linestring segments
      currentSegmentIndex: 0,
      segmentControllers: [], // Animation controllers for each segment
      preloadedSegments: new Map(), // Cache for preloaded segment data
      mapboxToken:
        "pk.eyJ1IjoiaGlpa2VyLW1hcHMiLCJhIjoiY2xydDBwMTU4MDFmdDJwbzd5NjFxMmlyciJ9.Q0bXbghB7N2Ji93oLrAJng",
      currentDistance: 0,
      currentElevation: 0,
      showLabels: true,
      // Whole-route vertices [lng, lat, elev?] + live playback progress (0..1) driving the elevation
      // profile's head dot. Set when a route is loaded; progress is read from the animation controller.
      flyoverCoords: [],
      // Vertex indices where the walk restarts (a gap between two of the hike's routes).
      flyoverBreaks: [],
      flyoverProgress: 0,
      animationController: null,
      isPlaying: true,
      finishedAnimation: false,
      playbackSpeed: 1,
      speedOptions: [1, 2, 3, 4, 5, 10],
      controllerSequence: 0,
      activeControllerId: null,
      // True only when this preview is showing a recording — gates the "Share video" toolbar button.
      isRecordedTrail: false,
      // Hike id when the preview was opened for a HIKE — reveals the same Share button, routed to the
      // Trail Trailer flow instead of the recording flyover.
      hikeTrailerId: null,
      // Share config for the current recording (recordingId/shareUrl/stats), passed in by whoever
      // opened the preview so the in-preview "Share video" button works from any entry point.
      recordedTrailShareConfig: null,
      // Distance/elevation/duration for the hike's share wizard, formatted off the GetHike the hike
      // entry already issues — a recording gets these from its page's data-* attributes, a hike had
      // no equivalent and the wizard showed em-dashes for all three.
      hikeShareStats: null,
    };
  },
  mounted() {
    this.$store.commit("mapStyles/setCurrentMapStyle", HiikerMapStyle.satellite());

    // Keep the same global function name for backward compatibility
    // Now it loads Hike data instead of Trail
    window.showMapModalForTrail = trail_id => {
      this.showModal();
      this.hikeTrailerId = trail_id;
      this.loading = true;
      graph_client.request(GetHike, { id: trail_id }).then(data => {
        // Use routes-only for flyover preview to show just the main route
        const hike = data.hike;
        this.hikeShareStats = hikeShareStats(hike);
        if (hike && hike.geojsonRoutesOnlyString) {
          const fc = JSON.parse(hike.geojsonRoutesOnlyString);
          // Collect all MultiLineString geometries and split them into LineString segments
          this.segments = [];
          const multiLineFeatures = (fc.features || []).filter(f => f.geometry && f.geometry.type === "MultiLineString");
          multiLineFeatures.forEach((f) => {
            this.segments.push(...this.parseMultilinestring(f.geometry));
          });
          // If no MultiLineString features found, use the feature collection as-is
          if (this.segments.length === 0) {
            this.featureCollection = fc;
          } else {
            this.featureCollection = fc; // keep as context for bounds/etc
          }
          this.initializeMapWhenReady();
        } else {
          // As a last resort, just center the map
          this.featureCollection = null;
          this.segments = [];
          this.initializeMapWhenReady();
        }
      });
    };

    window.showMapModalForPlannedTrail = planned_trail_id => {
      this.showModal();
      this.loading = true;
      graph_client
        .request(GetPtv2PlannedTrailLineString, { id: planned_trail_id })
        .then(data => {
          this.featureCollection = JSON.parse(
            data.ptv2PlannedTrailById.lineStringFeatureCollection
          );
          this.initializeMapWhenReady();
        });
    };

    window.showMapModalForRecordedTrail = (recorded_trail_id, shareConfig = null) => {
      this.showModal();
      this.isRecordedTrail = true;
      this.recordedTrailShareConfig = shareConfig;
      this.loading = true;
      graph_client
        .request(GetRecordedTrailLineStringById, { input: recorded_trail_id })
        .then(data => {
          this.featureCollection = JSON.parse(data.recording.lineString);
          this.initializeMapWhenReady();
        });
    };

    // Accept raw GeoJSON FeatureCollection directly (used by plan trail)
    window.showMapModalForCoordinates = (featureCollection) => {
      this.showModal();
      this.loading = true;
      this.segments = [];

      if (typeof featureCollection === "string") {
        featureCollection = JSON.parse(featureCollection);
      }

      // Check for MultiLineString features and split into segments
      const multiLineFeatures = (featureCollection.features || []).filter(
        f => f.geometry && f.geometry.type === "MultiLineString"
      );
      if (multiLineFeatures.length > 0) {
        multiLineFeatures.forEach((f) => {
          this.segments.push(...this.parseMultilinestring(f.geometry));
        });
      }

      // Also collect LineString features as segments
      const lineStringFeatures = (featureCollection.features || []).filter(
        f => f.geometry && f.geometry.type === "LineString"
      );
      if (lineStringFeatures.length > 0) {
        lineStringFeatures.forEach((f, index) => {
          this.segments.push({
            ...f,
            properties: { ...f.properties, segmentIndex: this.segments.length + index }
          });
        });
      }

      this.featureCollection = featureCollection;
      this.initializeMapWhenReady();
    };

    $("#mapModal").on("shown.bs.modal", () => {
      // The modal has finished its open transition, so the container is now at its final size. Gate
      // map creation on this: building the Mapbox map before this point makes it fall back to a
      // 400x300 default canvas anchored top-left, which is why the route appeared "in a corner".
      this.modalShown = true;
      if (window.flyover_map) {
        window.flyover_map.resize();
      }
    });

    $("#mapModal").on("hidden.bs.modal", () => {
      this.resetAnimationControllers();
      this.segments = [];
      this.currentSegmentIndex = 0;
      this.preloadedSegments.clear();
      if (window.flyover_map) {
        window.flyover_map.remove();
        window.flyover_map = null;
      }
      this.mapInitialized = false;
      this.modalShown = false;
      this.currentDistance = 0;
      this.currentElevation = 0;
      this.flyoverCoords = [];
      this.flyoverBreaks = [];
      this.flyoverProgress = 0;
      this.finishedAnimation = false;
      this.isPlaying = true;
      this.playbackSpeed = 1;
    });
  },
  beforeUnmount() {
    $("#mapModal").off("shown.bs.modal");
    $("#mapModal").off("hidden.bs.modal");
    delete window.showMapModalForRecordedTrail;
    delete window.showMapModalForPlannedTrail;
    delete window.showMapModalForTrail;
    delete window.showMapModalForCoordinates;
    this.resetAnimationControllers();
    if (window.flyover_map) {
      window.flyover_map.remove();
      window.flyover_map = null;
    }
  },
  methods: {
    showModal() {
      // Default to non-recording; the recorded-trail entry flips this on to reveal the Share button,
      // and the hike entry sets hikeTrailerId for the Trail Trailer flow.
      this.isRecordedTrail = false;
      this.hikeTrailerId = null;
      this.hikeShareStats = null;
      this.modalShown = false;
      const element = document.getElementById("mapModal");
      const modal = new Modal(element);
      modal.show();

      // Track mixpanel event for 3D preview (support both Hike/Trail naming during migration)
      if (window.mixpanel) {
        const name = window.hike_name || window.trail_name || window.trailName;
        const id = window.hike_id || window.trail_id || window.trailId || window.id;
        window.mixpanel.track('3D Preview Shown', {
          'Hike Name': name,
          'Hike ID': id,
          'Trail Name': name,
          'Trail ID': id
        });
      }

      // Same moment, scored for the web review prompt. Server-side the trigger name is
      // re-validated and deduped once per session, so this can safely fire on every open.
      claimReviewPromptTrigger('3d_preview_shown');
    },
    createControllerId() {
      this.controllerSequence += 1;
      return this.controllerSequence;
    },
    destroyAnimationController(controller) {
      if (!controller) return;
      if (typeof controller.destroy === "function") {
        controller.destroy();
        return;
      }
      if (typeof controller.stop === "function") {
        controller.stop();
      }
    },
    resetAnimationControllers() {
      this.activeControllerId = null;
      this.destroyAnimationController(this.animationController);
      this.animationController = null;
      this.segmentControllers.forEach(controller => {
        this.destroyAnimationController(controller);
      });
      this.segmentControllers = [];
    },
    applyPlaybackSpeed(controller = this.animationController) {
      if (!controller || typeof controller.setSpeedMultiplier !== "function") return;
      controller.setSpeedMultiplier(this.playbackSpeed);
    },
    // Mirror the animation controller's progress (0..1) onto the elevation profile's head dot. Called
    // from the per-frame progress callback, so it also stays in sync after fast-forward/rewind/restart.
    syncFlyoverProgress() {
      const p = this.animationController && typeof this.animationController.getProgress === "function"
        ? this.animationController.getProgress()
        : 0;
      this.flyoverProgress = p;
    },
    setPlaybackSpeed(speed) {
      this.playbackSpeed = speed;
      this.applyPlaybackSpeed();
    },
    // Open the flyover share wizard over this preview (stacked), reusing the recording data the share
    // button on the page already carries. Closing/finishing share returns to this 3D preview.
    openShareVideo() {
      const show = window.showFlyoverShare;
      if (typeof show !== "function") return;
      // The share video must match the style the user is looking at right now (they may have switched
      // it via the "Maps" dialog), so every path carries the preview's current map style — this is
      // what makes the wizard re-generate instead of replaying a stale render after a style change.
      const mapStyleUrl = this.currentMapStyleUrl();
      // Opened for a hike: share its Trail Trailer. Config comes from the hike page's globals; the
      // builder returns null without a hike id, in which case there is nothing to share.
      if (!this.isRecordedTrail && this.hikeTrailerId) {
        const cfg = buildHikeShareConfig(
          { ...window, hike_id: this.hikeTrailerId },
          window.location.href,
          mapStyleUrl,
          this.hikeShareStats
        );
        if (cfg) show({ ...cfg, source: SHARE_SOURCE });
        return;
      }
      // Prefer the config passed in when the preview was opened (works from any entry point, e.g. the
      // recordings list). Fall back to the page's "Generate 3D flyover" dataset / globals otherwise.
      if (this.recordedTrailShareConfig && this.recordedTrailShareConfig.recordingId) {
        show({ ...this.recordedTrailShareConfig, mapStyleUrl, source: SHARE_SOURCE });
        return;
      }
      const btn = document.querySelector(".generate-3d-flyover");
      const ds = (btn && btn.dataset) || {};
      show({
        recordingId: ds.recordingId || String(window.recordingId || ""),
        shareUrl: ds.shareUrl || window.recordingShareUrl || "",
        title: ds.title || window.recordingName || "",
        distance: ds.distance || "",
        elevation: ds.elevation || "",
        duration: ds.duration || "",
        thumbnailUrl: ds.thumbnailUrl || "",
        mapStyleUrl,
        source: SHARE_SOURCE,
      });
    },
    openMapsDialog() {
      if (window.user) {
        const mapStylesDialog = document.getElementById("flyover-map-styles-dialog");
        if (mapStylesDialog) {
          Modal.getOrCreateInstance(mapStylesDialog).show();
        }
      } else {
        window.onBoardingVue?.launchFor(OnBoardingGoal.MapStyles);
      }
    },
    currentMapStyleUrl() {
      return this.$store.state.mapStyles.currentMapStyle?.styleUrl || HiikerMapStyle.satellite().styleUrl;
    },
    parseMultilinestring(multilinestring) {
      // Parse GeoJSON MultiLineString into individual LineString segments
      const segments = [];

      if (multilinestring.type === "MultiLineString") {
        // Direct MultiLineString geometry
        multilinestring.coordinates.forEach((coords, index) => {
          segments.push({
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates: coords
            },
            properties: {
              segmentIndex: index
            }
          });
        });
      } else if (multilinestring.type === "FeatureCollection") {
        // FeatureCollection containing LineStrings
        multilinestring.features.forEach((feature, index) => {
          if (feature.geometry.type === "LineString") {
            segments.push({
              ...feature,
              properties: {
                ...feature.properties,
                segmentIndex: index
              }
            });
          }
        });
      } else if (multilinestring.type === "Feature" && multilinestring.geometry.type === "MultiLineString") {
        // Feature with MultiLineString geometry
        multilinestring.geometry.coordinates.forEach((coords, index) => {
          segments.push({
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates: coords
            },
            properties: {
              segmentIndex: index
            }
          });
        });
      }

      return segments;
    },
    // Create the map only once the fullscreen modal has finished opening. If the data resolved before
    // the open transition completed, defer to the `shown.bs.modal` event; otherwise init straight away.
    // Building it earlier lets Mapbox measure a not-yet-sized container and fall back to a 400x300
    // canvas pinned top-left — the cause of the flyover starting "in a corner".
    initializeMapWhenReady() {
      // Decision is a pure helper (unit-tested in flyoverMapInit.test.ts); this method only carries
      // out the DOM/event side effects for each outcome.
      const action = resolveFlyoverMapInit(this.mapInitialized, this.modalShown);
      if (action === "skip") return;
      if (action === "init") {
        this.initializeMap();
        return;
      }
      $("#mapModal").one("shown.bs.modal", () => this.initializeMap());
    },
    initializeMap() {
      if (this.mapInitialized) return;
      mapboxgl.accessToken = this.mapboxToken;
      window.flyover_map = new mapboxgl.Map({
        container: this.$refs.mapContainer,
        style: this.currentMapStyleUrl(),
        zoom: 5,
        pitch: 60,
        bearing: 0,
        antialias: true,
        // Support both historic window.lonlat and newer window.lonLat keys
        center: (window.lonlat || window.lonLat || [0.0, 0.0]),
      });
      window.flyover_map.on("style.load", () => {
        if (!window.flyover_map.getSource("mapbox-dem")) {
          window.flyover_map.addSource("mapbox-dem", {
            type: "raster-dem",
            url: "mapbox://mapbox.mapbox-terrain-dem-v1",
            tileSize: 512,
            maxzoom: 14
          });
        }
        window.flyover_map.setTerrain({ source: "mapbox-dem", exaggeration: 1.5 });
        // Resize BEFORE rendering so the intro camera frames against the real container size, not a
        // stale one, even if the style resolves faster than the layout settles.
        window.flyover_map.resize();
        this.renderFeatureCollection();
      });
      window.flyover_map.addControl(new mapboxgl.NavigationControl(), "bottom-right");
      this.mapInitialized = true;
    },
    clearLayers() {
      if (!window.flyover_map) return;
      if (window.flyover_map.getLayer("feature-shadow")) {
        window.flyover_map.removeLayer("feature-shadow");
      }
      if (window.flyover_map.getLayer("feature")) {
        window.flyover_map.removeLayer("feature");
      }
      if (window.flyover_map.getSource("feature-data")) {
        window.flyover_map.removeSource("feature-data");
      }
      if (window.flyover_map.getLayer("progressTrail")) {
        window.flyover_map.removeLayer("progressTrail");
      }
      // Both layers must go before the source: GL JS silently refuses to remove a source any layer
      // still references, and a surviving source makes the replay skip re-adding the route line.
      if (window.flyover_map.getLayer("progressTrailBorder")) {
        window.flyover_map.removeLayer("progressTrailBorder");
      }
      if (window.flyover_map.getSource("progressTrail")) {
        window.flyover_map.removeSource("progressTrail");
      }
    },
    renderFeatureCollection() {
      this.loading = false;
      if (!window.flyover_map) return;

      // Handle new segment-based rendering
      if (this.segments && this.segments.length > 0) {
        this.renderSegmentedTrail();
        return;
      }

      // Fallback to old rendering for non-segmented trails
      if (!this.featureCollection) {
        const center = window.lonLat || this.defaultCenter;
        window.flyover_map.flyTo({
          center: center,
          zoom: 12,
          pitch: 60,
          bearing: 0
        });
        return;
      }
      this.activeControllerId = null;
      this.destroyAnimationController(this.animationController);
      this.animationController = null;
      this.clearLayers();
      let geojson, coordinates = [];
      if (typeof this.featureCollection === "string") {
        try {
          geojson = JSON.parse(this.featureCollection);
        } catch (e) {
          console.error("Error parsing feature collection:", e);
          return;
        }
      } else {
        geojson = this.featureCollection;
      }
      if (geojson.type === "FeatureCollection") {
        for (const feature of geojson.features) {
          if (feature.geometry && feature.geometry.coordinates) {
            if (feature.geometry.type === "LineString") {
              coordinates = feature.geometry.coordinates;
              break;
            } else if (feature.geometry.type === "Point") {
              coordinates = [feature.geometry.coordinates];
              break;
            } else if (feature.geometry.type === "Polygon") {
              coordinates = feature.geometry.coordinates[0];
              break;
            }
          }
        }
      } else if (geojson.type === "Feature") {
        if (geojson.geometry && geojson.geometry.coordinates) {
          if (geojson.geometry.type === "LineString") {
            coordinates = geojson.geometry.coordinates;
          } else if (geojson.geometry.type === "Point") {
            coordinates = [geojson.geometry.coordinates];
          } else if (geojson.geometry.type === "Polygon") {
            coordinates = geojson.geometry.coordinates[0];
          }
        }
      }
      window.flyover_map.addSource("feature-data", { type: "geojson", data: geojson });
      const features = geojson.features || [geojson];
      const feature = features[0];
      const geometryType = feature.geometry ? feature.geometry.type : null;
      window.flyover_map.addLayer({
        id: "feature-shadow",
        type: geometryType === "Point" ? "circle" : "line",
        source: "feature-data",
        paint:
          geometryType === "Point"
            ? { "circle-radius": 12, "circle-color": "#000", "circle-opacity": 0.4, "circle-blur": 3 }
            : { "line-color": "#DB2B35", "line-width": 4 }
      });
      window.flyover_map.addLayer({
        id: "feature",
        type: geometryType === "Point" ? "circle" : "line",
        source: "feature-data",
        paint:
          geometryType === "Point"
            ? { "circle-radius": 8, "circle-color": "#FF4500", "circle-opacity": 1 }
            : { "line-color": "#DB2B35", "line-width": 8, "line-opacity": 1 }
      });
      if (geometryType === "LineString" && typeof previewTrail === "function" && coordinates.length > 0) {
        this.flyoverCoords = coordinates;
        const controllerId = this.createControllerId();
        this.animationController = previewTrail(
          window.flyover_map,
          coordinates,
          "medium",
          {},
          (distKm, elevM) => {
            const convertedDistance = MeasurementPrefUtility.preference().bigUnitFormatter(distKm);
            const convertedElv = MeasurementPrefUtility.preference().smallUnitFormatter(elevM);
            this.currentDistance = convertedDistance;
            this.currentElevation = convertedElv;
            this.syncFlyoverProgress();
          }
        );
        this.activeControllerId = controllerId;
        this.applyPlaybackSpeed();
        this.isPlaying = true;
        this.finishedAnimation = false;
        this.animationController.then(() => {
          if (this.activeControllerId !== controllerId) return;
          this.isPlaying = false;
          this.finishedAnimation = true;
        });
      } else {
        this.fitToCoordinates(coordinates);
      }
    },
    fitToCoordinates(coordinates) {
      if (!coordinates || coordinates.length === 0 || !window.flyover_map) return;
      const bounds = new mapboxgl.LngLatBounds();
      coordinates.forEach(coord => bounds.extend(coord));
      window.flyover_map.fitBounds(bounds, { padding: 50, maxZoom: 15, pitch: 60 });
    },
    renderSegmentedTrail() {
      this.currentSegmentIndex = 0;
      this.resetAnimationControllers();
      this.clearLayers();

      // The walk the flyover follows: the hike's routes oriented to follow one another, run
      // together where they meet, broken where they genuinely don't (see flyoverRoute.ts). Merging
      // routes in the hike editor says nothing about the direction each was drawn in — routes
      // merged end-to-end meet END to END — and taken as authored the flyover jumped back to the
      // far tip of each one, drawing a straight line the length of the route across the map.
      const route = buildFlyoverRoute(this.segments.map(seg => seg.geometry.coordinates));
      const multiLineCoordinates = route.strokes.map(stroke => stroke.coords);

      const fullTrailFeature = {
        type: "Feature",
        geometry: {
          type: "MultiLineString",
          coordinates: multiLineCoordinates
        }
      };

      window.flyover_map.addSource("feature-data", {
        type: "geojson",
        data: fullTrailFeature
      });

      window.flyover_map.addLayer({
        id: "feature-shadow",
        type: "line",
        source: "feature-data",
        paint: { "line-color": "#DB2B35", "line-width": 4 }
      });

      window.flyover_map.addLayer({
        id: "feature",
        type: "line",
        source: "feature-data",
        paint: { "line-color": "#DB2B35", "line-width": 8, "line-opacity": 1 }
      });

      // Relive: a single flyover over the WHOLE route instead of playing each segment separately,
      // so the flat-2D->3D intro, the orbiting follow, and the 2D zoom-out outro frame the entire
      // hike as one experience. The strokes go in as strokes, not flattened into one line: that is
      // what keeps the marker off the gaps and the pen up across them.
      if (route.coords.length < 2) {
        this.finishedAnimation = true;
        this.isPlaying = false;
        return;
      }
      this.flyoverCoords = route.coords;
      this.flyoverBreaks = route.breaks;

      const controllerId = this.createControllerId();
      this.animationController = previewTrail(
        window.flyover_map,
        multiLineCoordinates,
        "medium",
        {},
        (distKm, elevM) => {
          this.currentDistance = MeasurementPrefUtility.preference().bigUnitFormatter(distKm);
          this.currentElevation = MeasurementPrefUtility.preference().smallUnitFormatter(elevM);
          this.syncFlyoverProgress();
        }
      );
      this.activeControllerId = controllerId;
      this.applyPlaybackSpeed();
      this.isPlaying = true;
      this.finishedAnimation = false;
      this.animationController.then(() => {
        if (this.activeControllerId !== controllerId) return;
        this.isPlaying = false;
        this.finishedAnimation = true;
      });
    },
    playSegment(segmentIndex, startFromEnd = false) {
      if (segmentIndex >= this.segments.length) {
        this.finishedAnimation = true;
        this.isPlaying = false;
        return;
      }

      this.currentSegmentIndex = segmentIndex;
      const segment = this.segments[segmentIndex];

      // Get previous segment's ending state for smooth transition
      let previousEndState = null;
      if (segmentIndex > 0 && this.segmentControllers[segmentIndex - 1] && !startFromEnd) {
        previousEndState = this.segmentControllers[segmentIndex - 1].getEndState();
      }

      // If starting from end (rewinding from next segment), get next segment's starting state
      let nextStartState = null;
      if (startFromEnd && segmentIndex < this.segments.length - 1 && this.segmentControllers[segmentIndex + 1]) {
        nextStartState = this.segmentControllers[segmentIndex + 1].getStartState ?
          this.segmentControllers[segmentIndex + 1].getStartState() : null;
      }

      // Create animation controller for this segment
      const cameraOptions = {};

      if (startFromEnd && nextStartState) {
        // Starting from end when rewinding from next segment
        cameraOptions.initialBearing = nextStartState.bearing;
        cameraOptions.initialPitch = nextStartState.pitch;
        cameraOptions.startFromEnd = true;
      } else if (previousEndState) {
        // Normal forward progression
        cameraOptions.initialBearing = previousEndState.bearing;
        cameraOptions.initialPitch = previousEndState.pitch;
      }

      const controllerId = this.createControllerId();
      this.animationController = previewTrail(
        window.flyover_map,
        segment.geometry.coordinates,
        "medium",
        {},
        (distKm, elevM) => {
          // Calculate total distance including previous segments
          let totalDistance = distKm;
          for (let i = 0; i < segmentIndex; i++) {
            const prevSegment = this.segments[i];
            const prevLength = this.calculateSegmentLength(prevSegment.geometry.coordinates);
            totalDistance += prevLength;
          }

          const convertedDistance = MeasurementPrefUtility.preference().bigUnitFormatter(totalDistance);
          const convertedElv = MeasurementPrefUtility.preference().smallUnitFormatter(elevM);
          this.currentDistance = convertedDistance;
          this.currentElevation = convertedElv;
        },
        cameraOptions
      );

      this.activeControllerId = controllerId;
      this.segmentControllers[segmentIndex] = this.animationController;
      this.applyPlaybackSpeed();
      this.isPlaying = true;
      this.finishedAnimation = false;

      // When segment finishes, play the next one
      this.animationController.then(() => {
        if (this.activeControllerId !== controllerId) return;
        const isLastSegment = segmentIndex >= this.segments.length - 1;

        if (this.isPlaying) {
          if (!isLastSegment) {
            this.destroyAnimationController(this.segmentControllers[segmentIndex]);
            this.playSegment(segmentIndex + 1);

            if (segmentIndex + 2 < this.segments.length) {
              this.preloadSegment(segmentIndex + 2);
            }
          } else {
            this.finishedAnimation = true;
            this.isPlaying = false;
          }
        } else {
          if (!isLastSegment) {
            this.currentSegmentIndex = segmentIndex + 1;
            this.animationController = null;
          } else {
            this.finishedAnimation = true;
          }
        }
      });
    },
    preloadSegment(segmentIndex) {
      // Preprocess segment data in background
      if (segmentIndex < this.segments.length) {
        const segment = this.segments[segmentIndex];
        // Store preprocessed data for faster loading
        this.preloadedSegments.set(segmentIndex, {
          coordinates: segment.geometry.coordinates,
          preprocessed: true
        });
      }
    },
    calculateSegmentLength(coordinates) {
      let totalLength = 0;
      for (let i = 1; i < coordinates.length; i++) {
        const p1 = coordinates[i - 1];
        const p2 = coordinates[i];
        const R = 6371; // Earth radius in km
        const dLat = (p2[1] - p1[1]) * Math.PI / 180;
        const dLon = (p2[0] - p1[0]) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(p1[1] * Math.PI / 180) * Math.cos(p2[1] * Math.PI / 180) *
                Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        totalLength += R * c;
      }
      return totalLength;
    },
    jumpToSegment(segmentIndex, startFromEnd = false) {
      if (segmentIndex < 0 || segmentIndex >= this.segments.length) return;

      // Stop current animation
      this.activeControllerId = null;
      this.destroyAnimationController(this.animationController);

      // Clear any pending segment loads
      this.segmentControllers.forEach((controller, index) => {
        if (controller && index !== segmentIndex) {
          this.destroyAnimationController(controller);
        }
      });

      // Play the target segment
      this.playSegment(segmentIndex, startFromEnd);

      // Preload adjacent segments
      if (segmentIndex > 0) {
        this.preloadSegment(segmentIndex - 1);
      }
      if (segmentIndex < this.segments.length - 1) {
        this.preloadSegment(segmentIndex + 1);
      }
    },
    handleFastForward() {
      if (!this.animationController) return;

      // Check if we're near the end of current segment
      if (this.segments && this.segments.length > 0) {
        // Try to fast forward within current segment
        this.animationController.fastForward();

        // If at end of current segment and there's a next segment, jump to it
        // This would require checking animation progress, which we'll handle in the animation callback
      } else {
        // Use regular fast forward for non-segmented trails
        this.animationController.fastForward();
      }
    },
    handleRewind() {
      if (!this.animationController) return;

      if (this.segments && this.segments.length > 0) {
        // Try to rewind within current segment
        this.animationController.rewind();

        // If at beginning of current segment and there's a previous segment
        // we could implement jumping to previous segment here
      } else {
        // Use regular rewind for non-segmented trails
        this.animationController.rewind();
      }
    },
    togglePlayback() {
      const controller = this.animationController;

      if (!controller) {
        if (this.finishedAnimation) {
          this.restartAnimation();
        } else if (this.segments && this.segments.length > 0) {
          const nextIndex = Math.min(this.currentSegmentIndex, this.segments.length - 1);
          this.playSegment(nextIndex || 0);
          this.isPlaying = true;
          this.finishedAnimation = false;
        }
        return;
      }

      const progress = typeof controller.getProgress === 'function' ? controller.getProgress() : null;

      if (this.finishedAnimation && (progress === null || progress >= 1)) {
        this.restartAnimation();
        return;
      }

      if (this.isPlaying) {
        controller.pause();
        this.isPlaying = false;
        this.finishedAnimation = false;
      } else {
        if (progress !== null && progress >= 1) {
          this.restartAnimation();
        } else {
          controller.resume();
          this.isPlaying = true;
          this.finishedAnimation = false;
        }
      }
    },
    restartAnimation() {
      this.activeControllerId = null;
      this.renderFeatureCollection();
      this.finishedAnimation = false;
      this.isPlaying = true;
    },
  },
};
</script>

<style scoped>
.modal-fullscreen {
  width: 100vw;
  max-width: none;
  height: 100vh;
  margin: 0;
}
.modal-fullscreen .modal-content {
  height: 100vh;
  border: 0;
  border-radius: 0;
}
.close-button {
  position: absolute;
  top: 1em;
  right: 1em;
  z-index: 1000;
  width: 40px;
  height: 40px !important;
}
.play-button {
  position: relative;
  z-index: 1000;
}
.flyover-toolbar {
  position: absolute;
  top: 1em;
  right: 5.5em;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  max-width: calc(100% - 8.5em);
  z-index: 1000;
}
.speed-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}
.flyover-primary-buttons {
  display: flex;
  gap: 10px;
}
.flyover-pill-button {
  min-height: 40px;
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  font-weight: 600;
}
.control-buttons {
  display: flex;
  gap: 10px;
}
.control-button {
  width: 40px;
  height: 40px !important;
  display: flex;
  align-items: center;
  justify-content: center;
}
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 9999;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
}
.spinner-border {
  width: 4rem;
  height: 4rem;
}

@media (max-width: 768px) {
  /* Stack the toolbar into three single-line rows instead of letting the pills wrap raggedly:
       row 1 — speed pills (sit on the same line as the cancel/X button, top-right)
       row 2 — playback controls (rewind / play / forward)
       row 3 — Maps + Share video
     The toolbar starts at the top so its first row lines up with the X; `right: 60px` keeps that
     row clear of the X. The speed row scrolls horizontally if the pills overflow. */
  .flyover-toolbar {
    top: 1em;
    left: 10px;
    right: 60px;
    max-width: none;
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: stretch;
    gap: 8px;
  }
  .speed-buttons {
    order: 1;
    flex-wrap: nowrap;
    justify-content: flex-start;
    max-width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    padding-bottom: 2px;
  }
  .speed-buttons::-webkit-scrollbar {
    display: none;
  }
  .speed-buttons .flyover-pill-button {
    flex: 0 0 auto;
  }
  .control-buttons {
    order: 2;
    justify-content: flex-start;
  }
  .flyover-primary-buttons {
    order: 3;
    justify-content: flex-start;
  }
}
</style>
